import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BuildLabel } from "../components/BuildLabel";
import { ReleasePictures } from "../components/ReleasePictures";
import { api, type Build } from "../lib/api";
import { fmtAgo } from "../lib/format";

/** A prose section. Absent is stated rather than hidden: an empty page and an unwritten one look
 *  identical otherwise, and only one of them is somebody's job. */
function Section({ title, body, hint }: { title: string; body: string | null | undefined; hint: string }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>{title}</div>
      {body ? (
        <div style={{ whiteSpace: "pre-wrap" }}>{body}</div>
      ) : (
        <div className="muted" style={{ fontSize: 13 }}>Not written yet — {hint}</div>
      )}
    </div>
  );
}

function Editor({ build, onDone }: { build: Build; onDone: () => void }) {
  const [f, setF] = useState({
    releaseName: build.info?.release_name ?? "",
    description: build.info?.description ?? "",
    changes: build.info?.changes ?? "",
    testing: build.info?.testing ?? "",
    knownIssues: build.info?.known_issues ?? "",
  });
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => api.saveBuildInfo(build.id, f),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["builds"] });
      onDone();
    },
  });
  const field = (k: keyof typeof f) => ({ value: f[k], onChange: (e: any) => setF((p) => ({ ...p, [k]: e.target.value })) });

  return (
    <div className="panel" style={{ display: "grid", gap: 8 }}>
      {save.isError && <div className="error-bar">{String(save.error)}</div>}
      <label>
        Release name <span className="muted">(what the team calls it — “R2”)</span>
        <input {...field("releaseName")} placeholder="R2" />
      </label>
      <label>
        Description <span className="muted">(plain language: what this build is)</span>
        <textarea {...field("description")} rows={2} />
      </label>
      <label>
        What changed
        <textarea {...field("changes")} rows={3} />
      </label>
      <label>
        What needs testing <span className="muted">(the most useful field on this page)</span>
        <textarea {...field("testing")} rows={3} />
      </label>
      <label>
        Known issues <span className="muted">(so nobody re-reports what we already know)</span>
        <textarea {...field("knownIssues")} rows={2} />
      </label>
      <div className="row" style={{ gap: 8 }}>
        <button disabled={save.isPending} onClick={() => save.mutate()}>save</button>
        <button onClick={onDone}>cancel</button>
      </div>
    </div>
  );
}

export function BuildDetail() {
  const { buildId = "" } = useParams();
  const [editing, setEditing] = useState(false);
  const q = useQuery({ queryKey: ["builds", buildId], queryFn: () => api.build(buildId) });
  const b = q.data;

  if (q.isError) return <div className="error-bar">{String(q.error)}</div>;
  if (!b) return <div className="muted">loading…</div>;

  return (
    <div className="stack">
      <div className="muted" style={{ fontSize: 12 }}>
        <Link to="/builds">← all builds</Link>
      </div>
      <h2 style={{ marginBottom: 2 }}>
        <BuildLabel build={b} />
        {b.newest && <span className="chip" style={{ marginLeft: 8 }}>newest</span>}
      </h2>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
        published {fmtAgo(b.uploaded_ts)}
        {b.branch && <> · {b.branch}</>}
        {b.bytes && <> · {Math.round(b.bytes / 1024 / 1024)} MB</>}
      </div>

      {editing ? (
        <Editor build={b} onDone={() => setEditing(false)} />
      ) : (
        <div className="panel">
          <Section title="description" body={b.info?.description} hint="what is this build, in plain language?" />
          <Section title="what changed" body={b.info?.changes} hint="what was added or fixed?" />
          <Section title="what needs testing" body={b.info?.testing} hint="what should a tester actually go and do?" />
          <Section title="known issues" body={b.info?.known_issues} hint="what is already known broken, so nobody re-reports it?" />
          <button onClick={() => setEditing(true)}>{b.info ? "edit notes" : "write notes"}</button>
        </div>
      )}

      <h3>pictures</h3>
      <ReleasePictures />

      <h3>who is running it</h3>
      {b.devices.length ? (
        <ul>
          {b.devices.map((d) => (
            <li key={d.device_id}>
              {d.nickname || d.device_id} <span className="muted">· seen {fmtAgo(d.last_seen)}</span>
            </li>
          ))}
        </ul>
      ) : (
        /* Deliberately the weaker claim: the match is on a sha the publish script derives from the
           repo it ran in, so a build can carry a sha no install will ever report. */
        <div className="muted" style={{ fontSize: 13 }}>
          No device reports this build’s commit. That is not the same as nobody having installed it —
          a build published from a moved checkout can carry a commit no install will ever report.
        </div>
      )}
    </div>
  );
}

export function Builds() {
  const q = useQuery({ queryKey: ["builds"], queryFn: () => api.builds() });
  return (
    <div>
      <h2>builds</h2>
      <p className="muted" style={{ marginTop: -6 }}>
        Every build keeps its own page: what it is, what changed, and what needs testing.
      </p>
      {q.isError && <div className="error-bar">{String(q.error)}</div>}
      {q.data?.length === 0 && <div className="panel muted">No builds published yet.</div>}
      {q.data?.map((b) => (
        <Link key={b.id} to={`/builds/${encodeURIComponent(b.id)}`} className="panel card" style={{ display: "block", marginBottom: 8 }}>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
            <BuildLabel build={b} />
            {b.newest && <span className="chip">newest</span>}
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            published {fmtAgo(b.uploaded_ts)}
            {b.devices.length > 0 && <> · running on {b.devices.length} device{b.devices.length > 1 ? "s" : ""}</>}
          </div>
          {b.info?.testing && (
            <div style={{ marginTop: 6, fontSize: 13 }}>
              <span className="muted">needs testing: </span>
              {b.info.testing.length > 120 ? `${b.info.testing.slice(0, 120)}…` : b.info.testing}
            </div>
          )}
        </Link>
      ))}
    </div>
  );
}
